import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, message, Empty, Spin, Tag, Tooltip, CheckboxProps, ConfigProvider } from 'antd';
import {
  EyeOutlined,
  ClockCircleOutlined,
  CheckSquareOutlined,
  CheckSquareFilled,
  HourglassOutlined,
  UserSwitchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { BookmarkFilled, BookmarkOutlined } from '../../../components/icons/BookmarkIcon';
import {
  getOpportunities,
  getOpportunity,
  getOpportunityLocation,
  saveOpportunity,
  unsaveOpportunity,
} from '../../../services/api';
import { Opportunity, LLMRating, EmailTemplate, ContactStatus } from '../../../types';
import OpportunityDrawer from '../../../components/drawers/OpportunityDrawer';
import SourceTag from '../../../components/common/SourceTag';
import RejectModal, { RejectSubject, RejectSource } from '../../../components/modals/RejectModal';
import { getApiErrorMessage, isNotFoundError } from '../../../utils/errorUtils';
import { getPostingAge, STALE_TOOLTIP } from '../../../utils/postingAge';
import { useListPageState } from '../../../hooks/useListPageState';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useNotifications } from '../../../contexts/NotificationContext';
import { nowUnreadAnchor } from '../../../utils/unreadAnchor';
import { findApplicationIdForOpportunity } from '../../../utils/applicationForOpportunity';
import css from './OpportunitiesTab.module.scss';

interface OpportunitiesTabProps {
  searchId?: string;
  refreshKey?: number;
  onReject?: () => void;
  // Reports the scope-wide unread count (from the list envelope, kept in sync
  // by the optimistic mark-viewed decrement) so the parent can badge the tab.
  onUnreadCountChange?: (count: number) => void;
  // Reports the envelope total so the parent can gate the Auto-Apply offer
  // card on "the user actually has opportunities".
  onTotalCountChange?: (total: number) => void;
}

// antd ships row selection in blue throughout — the checkbox fill, the
// selected-row wash and its hover. Blue is wrong here for the reason the filter
// toggle's styles already give (nothing else in this product is blue), and the
// wash is worse than merely off-brand: it paints over the unread tint, so
// ticking an unread row erased the cue that says it is new.
//
// Set through antd's own component tokens rather than the stylesheet: antd's
// table styles live in ADOPTED stylesheets injected at runtime, which
// `document.styleSheets` cannot even enumerate — overriding them from CSS is a
// specificity race against a moving target, and two rounds of it lost.
//
// The wash is the heading ink at low alpha over white, NOT green. Green already
// carries two meanings on this row — saved (the rule on the leading edge) and
// unread (the tint) — and "selected" is a transient working state, not a
// verdict; a green wash would read as approval on the very rows the user is
// about to reject. The ticked box is the real signal. #e9e8ee is a clear step
// from the row hover's #f5f5f5. The CHECKBOX does take the brand green: it is
// the control being operated, and it answers the bookmark two columns over.
const TABLE_THEME = {
  components: {
    Table: {
      rowSelectedBg: '#e9e8ee',
      rowSelectedHoverBg: '#dcdbe1',
    },
    Checkbox: {
      colorPrimary: '#207551',
      colorPrimaryHover: '#185d40',
    },
  },
};

// Contact-status events (D19) arriving within this window of a refetch are
// folded into one more refetch at its end, so a resolution sweep that touches
// many opportunities costs two requests, not one per event.
const CONTACT_REFRESH_WINDOW_MS = 2000;

// Recipient verification, flagged on the row so a user scanning the list knows
// which opportunities can't be emailed yet without opening each one; the drawer
// explains and offers the next step. `ok` (and a legacy null) says nothing.
// Neutral for the wait (nothing to do), brand green for a suggestion (a person
// to approve), orange for "nobody to email" — the same "look before you act"
// tone as the eligibility chip. The icons are aria-hidden: the words carry it.
const contactStatusBadges: Record<
  Exclude<ContactStatus, 'ok'>,
  { label: string; color?: string; icon: React.ReactNode }
> = {
  checking: { label: 'Checking contact…', icon: <HourglassOutlined aria-hidden /> },
  proposal: { label: 'Contact suggestion', color: 'green', icon: <UserSwitchOutlined aria-hidden /> },
  unreachable: { label: 'No reachable contact', color: 'orange', icon: <StopOutlined aria-hidden /> },
};

const ratingConfig: Record<LLMRating, { stars: string; color: string; label: string }> = {
  perfect_fit: { stars: '★★★★★', color: '#52c41a', label: 'Perfect Fit' },
  strong_alignment: { stars: '★★★★☆', color: '#73d13d', label: 'Strong' },
  potential_candidate: { stars: '★★★☆☆', color: '#faad14', label: 'Potential' },
  partial_fit: { stars: '★★☆☆☆', color: '#fa8c16', label: 'Partial' },
  misalignment: { stars: '★☆☆☆☆', color: '#ff4d4f', label: 'Weak' },
};

const OpportunitiesTab: React.FC<OpportunitiesTabProps> = ({
  searchId,
  refreshKey,
  onReject,
  onUnreadCountChange,
  onTotalCountChange,
}) => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  // Narrows the list to saved rows only. `savedCount` is state, not a ref like
  // unreadCountRef: the toggle renders it, so a change has to re-render.
  const [savedOnly, setSavedOnly] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  // Bulk triage. Scoped to the rows on screen and cleared whenever the query
  // behind the list changes (see the effect below): acting on ids the user can
  // no longer see is the bulk-action failure mode worth designing out.
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  // Mobile only. The phone layout has a MEASURED three-column budget (see
  // mobileColumns), so a permanent checkbox column would push a column back
  // off-screen. Select mode spends the bookmark column on checkboxes instead —
  // you are triaging, not saving — and gives it back on exit. Desktop has the
  // width and shows checkboxes unconditionally.
  const [selectMode, setSelectMode] = useState(false);
  // The reject modal's subjects. Holding the subjects rather than deriving them
  // means the modal keeps naming exactly what it is about to reject even if a
  // refetch reorders or drops those rows underneath it.
  //
  // Openness is a SEPARATE flag, and the subjects deliberately outlive the
  // close: antd fades the modal out over ~200ms, and clearing them on close
  // would play that animation on an empty title and a `passing on ""?`
  // subtitle. They are invisible once the fade ends, and the next open
  // overwrites them.
  const [rejectSubjects, setRejectSubjects] = useState<RejectSubject[]>([]);
  const [rejectSource, setRejectSource] = useState<RejectSource>('row');
  const [rejectOpen, setRejectOpen] = useState(false);
  const { selectedId, page, pageSize, setPage, openDrawer, closeDrawer, redirectToBucket } =
    useListPageState({ basePath: '/opportunities' });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Last envelope unreadCount, decremented optimistically on mark-viewed. A
  // ref (not state): nothing here renders it — it's only relayed to the parent.
  const unreadCountRef = useRef(0);
  // Rows whose optimistic decrement already ran — makes handleViewed idempotent
  // (StrictMode double-fires the drawer's open effect in dev, and the state
  // guard alone can't see the first call's not-yet-committed update).
  const viewedSignaledRef = useRef<Set<string>>(new Set());

  // Two refs, because the save toggle has two distinct failure modes and they
  // have different lifetimes.
  //
  // `inFlightRef` — ids whose save/unsave request has not settled. The handler
  // reads its direction from `record.savedAt`, a render-time value, so two
  // clicks in the same tick both read "unsaved" and fire two requests; the
  // second is idempotent server-side but savedCount would count the save twice.
  // Membership bails the second click, and (mirrored into state below) disables
  // the button. Cleared the moment the request settles.
  //
  // `optimisticRef` — id → the savedAt this component last applied optimistically.
  // fetchOpportunities replaces the whole list, so a refetch issued before the
  // toggle re-commits the server's pre-toggle savedAt and, since the optimistic
  // path has no success-side patch, nothing ever puts the user's value back:
  // the row reads saved while the server holds it unsaved. Re-applying on commit
  // fixes that. It deliberately OUTLIVES the request — a refetch issued before
  // the toggle can land well after it settled — and is pruned on the first
  // commit that sees the id no longer in flight.
  //
  // KNOWN LIMIT: exactly one stale response is defended. Two responses that both
  // predate the toggle will defeat it — the first consumes the entry, the second
  // commits the server's pre-toggle value with nothing left to re-apply. Left
  // as-is deliberately: it needs two list fetches in flight across one toggle,
  // and the next fetch self-heals it.
  const inFlightRef = useRef<Set<string>>(new Set());
  const optimisticRef = useRef<Map<string, string | null>>(new Map());
  // Render mirror of inFlightRef. The ref is the authority (it has to be true
  // before React commits anything); this only drives the disabled attribute.
  const [inFlightIds, setInFlightIds] = useState<string[]>([]);
  const syncInFlight = useCallback(() => {
    setInFlightIds(Array.from(inFlightRef.current));
  }, []);

  // Unread/read grouping anchor, frozen for one browsing session. `viewedAt` is
  // the backend's primary sort key and opening the drawer stamps it, so under
  // OFFSET/LIMIT paging every row read on a page would shift the next page by
  // one and silently skip that many rows. Sending one anchor for the whole
  // session pins the grouping to the moment it started (unread = never viewed
  // OR viewed at/after the anchor), so a row read now keeps its position until
  // the next session — while the tint keeps following the real viewedAt and
  // untints immediately.
  //
  // A new session is: this view mounting, a search-filter change, a flip of the
  // Saved filter, or a socket/action-driven refresh (refreshKey). Paging is NOT.
  // Derived during render rather than in an effect so the first fetch of a new
  // session already carries the new anchor — an effect would fire one request
  // with the stale one.
  const sessionKey = `${searchId ?? ''}|${refreshKey ?? 0}|${savedOnly ? 's' : ''}`;
  const anchorRef = useRef<{ sessionKey: string; anchor: string } | null>(null);
  if (anchorRef.current === null || anchorRef.current.sessionKey !== sessionKey) {
    anchorRef.current = { sessionKey, anchor: nowUnreadAnchor() };
  }
  const unreadAnchor = anchorRef.current.anchor;

  // The opportunity the drawer opened on, pinned in its own state. Opening the
  // drawer stamps viewed_at server-side, so a later refetch can reorder the row
  // off the current page; a purely list-derived lookup would then go null
  // mid-edit and unmount the editor, silently destroying unsaved cover-letter
  // edits (the Regenerate path guards those very fields with a blocking
  // "Replace your edits?" confirm — the refetch path must not be less careful).
  const [openedOpportunity, setOpenedOpportunity] = useState<Opportunity | null>(null);

  const selectedOpportunity = useMemo<Opportunity | null>(() => {
    if (!selectedId) return null;
    // Once pinned, the open drawer reads only from the pinned copy.
    if (openedOpportunity?.id === selectedId) return openedOpportunity;
    return opportunities.find(o => o.id === selectedId) ?? null;
  }, [opportunities, selectedId, openedOpportunity]);
  const drawerOpen = Boolean(selectedOpportunity);

  // Pin on open, release on close. Keyed on the id, so list updates for an
  // already-open row (the optimistic viewedAt patch, a refetch that drops or
  // reorders it) never re-pin and never disturb the drawer.
  useEffect(() => {
    setOpenedOpportunity(selectedOpportunity);
  }, [selectedOpportunity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server rows win, EXCEPT for a save the user has already made and the row
  // predates — see optimisticRef. `has` is the guard rather than
  // `?? o.savedAt`: the optimistic value for an unsave is legitimately null,
  // and a nullish fallback would discard exactly that case.
  const withOptimisticSave = useCallback((o: Opportunity): Opportunity =>
    optimisticRef.current.has(o.id)
      ? { ...o, savedAt: optimisticRef.current.get(o.id) ?? null }
      : o, []);

  // The open drawer's opportunity id, readable from async callbacks.
  const openIdRef = useRef<string | undefined>(undefined);
  openIdRef.current = selectedOpportunity?.id;

  // Copies of the open opportunity arrive from several requests in flight at
  // once — list refreshes, the contact-event refetch (the throttle's first and
  // trailing runs), the drawer's own refetch and accept — and in any order.
  // They are ordered by when each request was ISSUED, not when it answered:
  // every request that can update the pinned copy takes a ticket when it
  // starts, and its answer replaces the pin only if no later-issued request's
  // answer has already done so. An older answer landing last would otherwise
  // put back an old contact (reloading the old letter over the user's edits
  // to the new one) — and a newer answer must never be discarded because an
  // older one happened to land first: if the row then moves off the list's
  // page, nothing else would bring the drawer up to date.
  //
  // The ticket is kept PER OPPORTUNITY: the user can leave A for B and come
  // back while A's slow request is still out, and A's answer must still lose
  // to the newer copy of A on screen. The cached rows obey it too — the drawer
  // reopens from them. A list answer counts as a copy of every row it holds,
  // and of nothing else. An answer that loses here is overtaken by a
  // later-issued request's answer, which is at least as fresh, so nothing has
  // to be fetched again. Entries are a few bytes each and live as long as the
  // page; they are never pruned.
  const pinTicketRef = useRef(0);
  const appliedTicketsRef = useRef(new Map<string, number>());
  // Records `ticket` as the opportunity's current copy and says whether the
  // answer may be applied: false when a later-issued request has already
  // answered for it.
  const claimCopy = useCallback((opportunityId: string, ticket: number) => {
    const applied = appliedTicketsRef.current.get(opportunityId);
    if (applied !== undefined && applied > ticket) return false;
    appliedTicketsRef.current.set(opportunityId, ticket);
    return true;
  }, []);

  // `isCancelled` is supplied by the effect below, whose params (page, filter,
  // session anchor) can move on while a request is in flight — the late
  // response would then overwrite the newer list AND push a stale count into
  // the parent's unread badge. Same cancelled-flag pattern as the deep-link
  // effect further down. Imperative callers (handleActionComplete) pass
  // nothing: they are one-shot refreshes with no superseding request.
  //
  // `silent` is for refreshes the user didn't ask for (a contact-status event):
  // the table doesn't spin under them and a failure isn't toasted — the rows on
  // screen stay. It still clears the spinner when it lands, since it supersedes
  // any request that was showing one.
  const fetchOpportunities = useCallback(async (
    isCancelled?: () => boolean,
    { silent = false }: { silent?: boolean } = {}
  ) => {
    if (!silent) setLoading(true);
    const ticket = ++pinTicketRef.current;
    try {
      // No explicit sort: the backend defaults to unread-first (then newest),
      // giving the list its email-inbox ordering. The anchor freezes that
      // grouping for the session so paging can't skip rows.
      const response = await getOpportunities({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        searchId,
        unread_anchor: unreadAnchor,
        savedOnly,
      });
      if (isCancelled?.()) return;
      const rows = response.opportunities.map(withOptimisticSave);
      // A row a later-issued request has already answered for keeps that
      // copy (see pinTicketRef).
      const overtaken = new Set(rows.filter(o => !claimCopy(o.id, ticket)).map(o => o.id));
      setOpportunities(prev => {
        if (overtaken.size === 0) return rows;
        const cached = new Map(prev.map(o => [o.id, o]));
        return rows.map(o => (overtaken.has(o.id) ? cached.get(o.id) ?? o : o));
      });
      // The open drawer reads only its pinned copy, so a refresh (the socket's
      // included) has to reach it explicitly, or the drawer keeps showing a
      // contact the server has since replaced — and would send a draft bound to
      // it. Only a row this response actually holds replaces the pin: one it
      // dropped stays pinned (see openedOpportunity). And only if no request
      // issued after this one has already answered for it (see pinTicketRef).
      // The drawer itself keeps the user's edits unless the contact changed.
      const openRow = rows.find(o => o.id === openIdRef.current);
      if (openRow && !overtaken.has(openRow.id)) {
        setOpenedOpportunity(prev => (prev && prev.id === openRow.id ? openRow : prev));
      }
      // Applied once, then dropped — unless the request is still running, in
      // which case a later response could still predate it.
      optimisticRef.current.forEach((_, id) => {
        if (!inFlightRef.current.has(id)) optimisticRef.current.delete(id);
      });
      setTotal(response.total);
      setSavedCount(response.savedCount ?? 0);
      // Prune the duplicate-signal guard: a row the server still reports
      // unread was never actually marked (the POST /view failed), so its
      // optimistic patch must be allowed to run again on the next open —
      // otherwise the row would stay tinted until a second refetch.
      response.opportunities.forEach(o => {
        if (o.viewedAt == null) viewedSignaledRef.current.delete(o.id);
      });
      // Scope-narrowed counts are not the Pending tab's numbers: with the
      // filter on, `unreadCount` means "unread AND saved" and `total` is the
      // saved subset — which also gates the parent's Auto-Apply offer card.
      // Keep the parent's last unfiltered values while the Saved filter is on.
      // unreadCountRef is held back for the same reason: handleViewed relays it
      // unconditionally, so overwriting it here would leak the narrowed count
      // into the badge on the next drawer open. Its optimistic decrement stays
      // meaningful — a row read under the filter is one fewer unread overall.
      if (!savedOnly) {
        unreadCountRef.current = response.unreadCount ?? 0;
        onUnreadCountChange?.(unreadCountRef.current);
        onTotalCountChange?.(response.total);
      }
    } catch (error) {
      if (isCancelled?.()) return;
      console.error('Fetch opportunities error:', error);
      if (!silent) message.error(getApiErrorMessage(error));
    } finally {
      // A superseded request must not clear the spinner the live one is
      // still showing.
      if (!isCancelled?.()) setLoading(false);
    }
    // `refreshKey` is listed but never read here, and that is the point: it is
    // the parent's refetch channel — Opportunities.tsx bumps it on an
    // `opportunities_ready` socket event and on Undo from the Rejected tab —
    // and this callback's identity is how that reaches the effect below.
    // Excluding it, which is what the rule asks for, would leave the refetch
    // riding on `unreadAnchor`, which only moves with it because `sessionKey`
    // folds `refreshKey` in. Naming the trigger directly is deliberate; the
    // anchor ref is not a channel to depend on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchId, refreshKey, unreadAnchor, savedOnly, onUnreadCountChange, onTotalCountChange, withOptimisticSave, claimCopy]);

  // ONE generation counter for every fetch, effect-driven or imperative, so only
  // the most recent request can commit. It replaces a per-effect `cancelled`
  // flag, which covered the effect path but left the imperative refreshes
  // (reject, undo, unsave-in-filter) racing each other unguarded: a reject's GET
  // landing after its Undo's GET would momentarily re-hide the row the user had
  // just restored. Bumping in cleanup keeps the old semantics — a param change
  // or an unmount invalidates whatever is in flight.
  const fetchGenRef = useRef(0);

  useEffect(() => {
    const gen = ++fetchGenRef.current;
    fetchOpportunities(() => gen !== fetchGenRef.current);
    // The rule's advice — copy `fetchGenRef.current` into a local and use that
    // in the cleanup — is the one change that would break this. The cleanup has
    // to bump the LIVE counter so the in-flight request's `gen !== current`
    // check fails; a value snapshotted at effect start would be bumped into a
    // stale local and the superseded response would commit over the new one.
    // The ref itself is created once and never reassigned.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { fetchGenRef.current++; };
  }, [fetchOpportunities]);

  // Imperative refreshes have to run the CURRENT query, not the one that was
  // current when their callback was built. The reject toast captures its handler
  // once and lives for 8 seconds: an Undo taken after switching search would
  // otherwise refetch the OLD search and commit its rows over the list on
  // screen. The ref is what makes "current" mean current.
  const fetchRef = useRef(fetchOpportunities);
  useEffect(() => { fetchRef.current = fetchOpportunities; }, [fetchOpportunities]);

  const refresh = useCallback((opts?: { silent?: boolean }) => {
    const gen = ++fetchGenRef.current;
    fetchRef.current(() => gen !== fetchGenRef.current, opts);
  }, []);

  // A selection only means anything over the rows it was made on. Paging,
  // filtering or switching search replaces those rows, so the checkboxes have to
  // let go — otherwise "Reject 6" would reject six jobs the user is no longer
  // looking at. Deliberately NOT keyed on the imperative refetch after a bulk
  // reject: that one keeps the ids that failed selected so the retry is a click.
  // `pageSize` belongs here too: changing rows-per-page re-cuts the page under
  // the ticks. Selecting row 15 of 20 and then switching to 10 per page left the
  // bulk bar counting a row that is no longer rendered — and openBulkReject,
  // which resolves ids against the loaded rows, silently dropped it. The bar
  // said 3, the modal said 2.
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [page, pageSize, savedOnly, searchId, refreshKey]);

  useEffect(() => {
    if (page !== 1) setPage(1, undefined, { replace: true });
  }, [searchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A page past the end — opportunities removed elsewhere, so a refresh of the
  // last page comes back empty while others remain — moves to the last page
  // that has rows. Only the table's page: the URL keeps the open id, and the
  // pinned drawer and its editor are left alone.
  useEffect(() => {
    if (loading || opportunities.length > 0 || total === 0) return;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > lastPage) setPage(lastPage, undefined, { replace: true });
    // `setPage` is rebuilt on every URL change (see the deep-link effect below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, opportunities, total, page, pageSize]);

  // A deep link outranks the filter. getOpportunityLocation counts under the
  // backend's unfiltered pending scope (count_opportunities_before knows nothing
  // about saved_only), and the target may not be saved at all — so with the
  // filter on it returns a page no filtered list can contain, and the drawer
  // never opens. A deep link's job is to open that opportunity; the unfiltered
  // list always can.
  //
  // But opening a row on screen ALSO sets selectedId, and clearing the filter
  // there threw the user out of the triage list they were working — tap a saved
  // row, close the drawer, and every pending row is back. The precondition is
  // not "an id was selected", it is "the unfiltered locator has to be consulted",
  // and the effect below only consults it for an id the loaded rows don't hold.
  // Mirror that test here, so a visible row keeps the filter.
  useEffect(() => {
    if (!selectedId || !savedOnly) return;
    if (opportunities.some(o => o.id === selectedId)) return;
    setSavedOnly(false);
    // Keyed on selectedId alone, deliberately: `opportunities` is read but must
    // NOT be a dep. It changes on every refetch, and re-running then would clear
    // the filter the moment a refetch drops the open row from the filtered page
    // — which is precisely the case the pinned drawer copy exists to survive.
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve deep links: locate the id and redirect to /rejected if needed.
  useEffect(() => {
    if (!selectedId) return;
    if (loading) return;
    // The drawer is already open on this id and holds its own pinned copy —
    // a refetch that reordered the row off the page must not re-locate, which
    // would jump the table and reopen the drawer re-seeded from the server.
    if (openedOpportunity?.id === selectedId) return;
    if (opportunities.some(o => o.id === selectedId)) return;

    let cancelled = false;
    (async () => {
      try {
        // Same session anchor as the list, so the located page matches the
        // page the list would actually render.
        const loc = await getOpportunityLocation(selectedId, pageSize, unreadAnchor);
        if (cancelled) return;
        if (loc.bucket === 'rejected') {
          redirectToBucket('/opportunities/rejected', selectedId, loc.page);
          return;
        }
        if (loc.page !== page) {
          setPage(loc.page, undefined, { replace: true });
        }
      } catch (error) {
        if (cancelled) return;
        // A sent opportunity has no pending page (404), but a link to it can
        // outlive that — a contact notification published before auto-apply
        // sent it. Land on the application it became; only an opportunity
        // with none is "not found".
        if (isNotFoundError(error)) {
          let applicationId: string | null;
          try {
            applicationId = await findApplicationIdForOpportunity(selectedId);
          } catch (lookupError) {
            // The lookup failed, which says nothing about whether the
            // opportunity exists. Say why and stay on the link, so a reload
            // can try again.
            if (cancelled) return;
            message.error(getApiErrorMessage(lookupError));
            return;
          }
          if (cancelled) return;
          if (applicationId) {
            navigate(`/applications/${applicationId}`, { replace: true });
            return;
          }
        }
        message.error('Opportunity not found');
        navigate('/opportunities', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // The pagination handles are read but not listed, on purpose. `setPage`,
    // `redirectToBucket` and `navigate` are all rebuilt whenever the URL
    // changes — useListPageState keys them on `searchParams` — and changing the
    // URL is exactly what this effect does. Listing them, or `page`/`pageSize`,
    // re-enters the effect from its own `setPage` before the refetch it
    // triggers has landed, firing a second getOpportunityLocation for an id
    // that is already being located.
    // `unreadAnchor` is left out for the same reason it exists: it is a
    // per-session ref value that only changes when the session key does, and
    // every one of those changes already refetches the list, which is one of
    // the deps below. `loading` and the list are the honest triggers — they are
    // what tells us whether the id is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, opportunities, loading, openedOpportunity?.id]);

  const handleViewDetails = (record: Opportunity) => {
    openDrawer(record.id);
  };

  const handleDrawerClose = () => {
    closeDrawer();
  };

  const handleActionComplete = (action?: 'reject' | 'apply') => {
    refresh();
    handleDrawerClose();
    if (action === 'reject') onReject?.();
  };

  const subjectOf = (record: Opportunity): RejectSubject => ({
    id: record.id,
    title: record.title,
    company: record.company.name,
  });

  // Every control that names a row names its company too. "Senior Backend
  // Engineer" is not a distinguishing name — two companies advertising the same
  // title is the ordinary case, and the company is usually the thing that made
  // one of them wrong. Sighted users read it from the cell; this is the only way
  // a screen reader gets it.
  const rowName = (record: Opportunity) => `${record.title} at ${record.company.name}`;

  // Rejecting from the row deliberately does NOT mark the opportunity viewed:
  // no drawer opens, so nothing POSTs /view. That is what makes the rejection
  // reversible without side effects — an Undo puts the row back exactly as it
  // was, still unread, instead of quietly filing it under "already seen".
  const openReject = (subjects: RejectSubject[], source: RejectSource) => {
    setRejectSubjects(subjects);
    setRejectSource(source);
    setRejectOpen(true);
  };

  const openRowReject = (record: Opportunity) => openReject([subjectOf(record)], 'row');

  const openBulkReject = () => {
    // Resolved against the loaded rows rather than carried on the selection, so
    // a title can never go stale. Ids the current page no longer holds are
    // dropped: the effect above normally prevents that, and silently rejecting
    // an unresolvable id would be worse than skipping it.
    const byId = new Map(opportunities.map(o => [o.id, o]));
    const subjects = selectedRowKeys
      .map(id => byId.get(id))
      .filter((o): o is Opportunity => Boolean(o))
      .map(subjectOf);
    if (subjects.length === 0) return;
    openReject(subjects, 'bulk');
  };

  // Runs after a reject AND again after an undo (which passes no ids). Both
  // changed this list and the Rejected tab, so both refresh both. Only the
  // ids that actually went through leave the selection — a partial failure
  // keeps the rest ticked, which is also the retry.
  const handleRejectSuccess = useCallback((rejectedIds: string[]) => {
    if (rejectedIds.length > 0) {
      const done = new Set(rejectedIds);
      // Drop the rows NOW rather than waiting for the GET. Until it landed they
      // were still on screen with a live Reject button, so a second click sent a
      // second POST for an already-rejected id — and a slow or failed GET left
      // them sitting there as if nothing had happened. Same shape as the
      // unsave-inside-the-filter path above. `total` self-corrects on the
      // refresh a moment later.
      setOpportunities(prev => prev.filter(o => !done.has(o.id)));
      setSelectedRowKeys(prev => prev.filter(id => !done.has(id)));
    }
    refresh();
    onReject?.();
  }, [refresh, onReject]);

  // Regenerate persists server-side; patch the cached row so a close+reopen
  // shows the regenerated template rather than the stale original.
  //
  // Only a record still bound to the contact the letter was written for. A list
  // response can queue the record's switch to another contact in the same batch
  // the regeneration answers in, before the drawer has seen it; patching that
  // record would hand the drawer the old contact's letter under the new
  // contact's id, and the send would go to the wrong person. The functional
  // updates see the queued record, so the check is made against it.
  const handleRegenerated = useCallback((
    opportunityId: string,
    template: EmailTemplate,
    contactId: string | null
  ) => {
    const isTarget = (o: Opportunity) =>
      o.id === opportunityId && (o.contact?.id ?? null) === contactId;
    setOpportunities(prev => prev.map(o => (isTarget(o) ? { ...o, template } : o)));
    // Patch the pinned copy too — it is what the open drawer renders from.
    setOpenedOpportunity(prev => (prev && isTarget(prev) ? { ...prev, template } : prev));
  }, []);

  // A fresh copy of the open opportunity replaces both the row and the pin, so
  // the drawer shows the contact's new state at once.
  const applyFresh = useCallback((fresh: Opportunity) => {
    const row = withOptimisticSave(fresh);
    setOpportunities(prev => prev.map(o => (o.id === row.id ? row : o)));
    setOpenedOpportunity(prev => (prev && prev.id === row.id ? row : prev));
  }, [withOptimisticSave]);

  // Runs a request that answers with a fresh copy of the drawer's opportunity
  // (a refetch, an accepted suggestion) and applies it — unless a request
  // issued after it (another such request, a list refresh carrying the row)
  // has already answered: then it is dropped and this resolves null (see
  // pinTicketRef). Patches only the row and pin with the copy's id, so a drawer
  // that moved on meanwhile is left alone. A failure is thrown to the caller.
  const loadDrawerCopy = useCallback(async (request: () => Promise<Opportunity>) => {
    const ticket = ++pinTicketRef.current;
    const fresh = await request();
    if (!claimCopy(fresh.id, ticket)) return null;
    applyFresh(fresh);
    return fresh;
  }, [applyFresh, claimCopy]);

  // The drawer's own requests (a refetch after a send, regenerate or accept was
  // refused over the contact; the accept itself), on the same numbering as the
  // background refetch, so an answer that a newer copy has overtaken neither
  // reaches the pin nor reloads the drawer's letter. Then a list refresh,
  // whatever happens: it supersedes any list request already in flight, which
  // predates this copy and would otherwise land after it and put the old
  // contact back — the newest request wins (refresh() bumps the generation AND
  // clears its own spinner, which a bare bump of fetchGenRef would leave
  // stuck). And when this answer was dropped, the copy that overtook it may
  // predate what the server just said.
  //
  // An overtaken answer gets one more look, by id: the later-issued request that
  // beat it may have read the row before this answer's change committed (a list
  // refresh started just after an accept, still showing the proposal), and if
  // the row is off the list's page no later list refresh will correct the
  // drawer. The refetch takes its own ticket, so it too loses to anything newer.
  const handleDrawerLoadFresh = useCallback(async (request: () => Promise<Opportunity>) => {
    try {
      let answered: Opportunity | null = null;
      const applied = await loadDrawerCopy(async () => (answered = await request()));
      if (applied || !answered) return applied;
      const { id } = answered as Opportunity;
      try {
        return await loadDrawerCopy(() => getOpportunity(id));
      } catch (error) {
        // The request itself succeeded; a failed follow-up look is not its
        // failure. The list refresh below still runs.
        console.error('Refetch after an overtaken drawer copy failed:', error);
        return null;
      }
    } finally {
      refresh();
    }
  }, [loadDrawerCopy, refresh]);

  // The drawer's writes (an accept): read-after-write. A write's answer is a
  // snapshot from some moment the browser can't know — before or after a
  // later change (a resolution that moved on to yet another contact) — so it
  // is never applied to the pin or the rows. Instead, once the write has
  // completed, every read of that opportunity issued before then is
  // invalidated (it may predate the write), and one read issued after it is
  // applied through the usual guard: it is at least as new as the write and
  // loses only to something issued later still. A failure of that read is
  // reported, not thrown: the write itself went through.
  const handleDrawerWrite = useCallback(async (write: () => Promise<Opportunity>) => {
    const written = await write();
    claimCopy(written.id, ++pinTicketRef.current);
    try {
      return { written, fresh: await handleDrawerLoadFresh(() => getOpportunity(written.id)) };
    } catch (readError) {
      return { written, fresh: null, readError };
    }
  }, [claimCopy, handleDrawerLoadFresh]);

  // Recipient verification (D19): an opportunity's contact status moved on the
  // server. The event carries no state, so ask for it: the open drawer's
  // opportunity by id (it may not be on this page of the list), then the list.
  // The drawer is refetched whatever the event names — the provider debounces
  // per subscriber, so of several changes landing together only the last
  // arrives, and the one dropped could be the open one. The toast types are
  // listened for too, so the page is right even if one comes without its
  // refresh event.
  //
  // Silent throughout: nobody asked for this, so no table spinner and no error
  // toast. A failed drawer refetch leaves the drawer on its last copy and still
  // refreshes the list.
  const { subscribe } = useNotifications();
  const refetchAfterContactChange = useCallback(async () => {
    const openId = openIdRef.current;
    if (openId) {
      try {
        // Superseded, the copy is dropped; the list refresh below still runs,
        // because the copy that superseded it may be a list response that
        // predates this event.
        await loadDrawerCopy(() => getOpportunity(openId));
      } catch (error) {
        console.error('Refetch after a contact status change failed:', error);
      }
    }
    // After the drawer copy, so this request is the newest and wins over any
    // list request already in flight (see handleDrawerLoadFresh).
    refresh({ silent: true });
  }, [loadDrawerCopy, refresh]);

  // A resolution sweep publishes a change per opportunity, so events come in
  // bursts. Throttled, leading and trailing: the first event refetches at once,
  // and everything that lands in the next window is folded into one more
  // refetch at its end.
  const contactThrottleRef = useRef<{ last: number; timer: ReturnType<typeof setTimeout> | null }>(
    { last: 0, timer: null }
  );
  useEffect(() => {
    const throttle = contactThrottleRef.current;
    const unsubscribe = subscribe(
      ['contact_status_changed', 'contact_switched', 'contact_switch_proposed', 'contact_unreachable'],
      () => {
        if (throttle.timer) return;
        const wait = throttle.last + CONTACT_REFRESH_WINDOW_MS - Date.now();
        if (wait <= 0) {
          throttle.last = Date.now();
          refetchAfterContactChange();
          return;
        }
        throttle.timer = setTimeout(() => {
          throttle.timer = null;
          throttle.last = Date.now();
          refetchAfterContactChange();
        }, wait);
      }
    );
    return () => {
      unsubscribe();
      if (throttle.timer) clearTimeout(throttle.timer);
      throttle.timer = null;
    };
  }, [subscribe, refetchAfterContactChange]);

  // Optimistic mark-as-read: the drawer fired POST /view; untint the row and
  // drop the tab badge by one immediately, without waiting for a refetch (the
  // next fetch returns the server-authoritative viewedAt/unreadCount anyway).
  const handleViewed = useCallback((opportunityId: string) => {
    if (viewedSignaledRef.current.has(opportunityId)) return;
    viewedSignaledRef.current.add(opportunityId);
    setOpportunities(prev =>
      prev.map(o =>
        o.id === opportunityId && o.viewedAt == null
          ? { ...o, viewedAt: new Date().toISOString() }
          : o
      )
    );
    unreadCountRef.current = Math.max(0, unreadCountRef.current - 1);
    onUnreadCountChange?.(unreadCountRef.current);
  }, [onUnreadCountChange]);

  // Explicit user gesture, so unlike mark-viewed this is NOT fire-and-forget:
  // fill optimistically, and put it back if the server refuses. Both the list
  // row and the pinned drawer copy are patched — the open drawer renders only
  // from the pinned copy (see openedOpportunity).
  const handleToggleSaved = useCallback(async (record: Opportunity) => {
    // Second click of a double-click: the direction comes from a render-time
    // value, so this one would read the same stale "unsaved" and double-count.
    if (inFlightRef.current.has(record.id)) return;
    const wasSaved = record.savedAt != null;
    const optimistic = wasSaved ? null : new Date().toISOString();
    const patch = (value: string | null) => {
      setOpportunities(prev =>
        prev.map(o => (o.id === record.id ? { ...o, savedAt: value } : o))
      );
      setOpenedOpportunity(prev =>
        prev && prev.id === record.id ? { ...prev, savedAt: value } : prev
      );
    };

    inFlightRef.current.add(record.id);
    optimisticRef.current.set(record.id, optimistic);
    syncInFlight();
    patch(optimistic);
    try {
      await (wasSaved ? unsaveOpportunity(record.id) : saveOpportunity(record.id));
      setSavedCount(prev => Math.max(0, prev + (wasSaved ? -1 : 1)));
      // The row no longer matches the query it is being rendered under. Leaving
      // it is a lie, and it skews `total` and every page after this one.
      if (wasSaved && savedOnly) {
        setOpportunities(prev => prev.filter(o => o.id !== record.id));
        refresh();
      }
    } catch (error) {
      patch(wasSaved ? record.savedAt ?? null : null);
      // Rolled back, so there is no optimistic value left to defend.
      optimisticRef.current.delete(record.id);
      // Name the subject and the direction: the row that rolled back may have
      // scrolled out of view, and a bare transport error leaves the user unable
      // to tell what failed or which way. `key` collapses repeats per row so an
      // offline user clicking several bookmarks gets one toast each, not N.
      message.error({
        key: `save-${record.id}`,
        content: `${wasSaved ? "Couldn't remove that from saved" : "Couldn't save that"} — ${getApiErrorMessage(error)}`,
      });
    } finally {
      inFlightRef.current.delete(record.id);
      syncInFlight();
    }
  }, [savedOnly, refresh, syncInFlight]);

  const ratingColumn = {
    title: 'Rating',
    dataIndex: 'llmRating',
    key: 'rating',
    width: isMobile ? 76 : 120,
    render: (rating: LLMRating) => {
      const config = ratingConfig[rating];
      return (
        <span className={css.stars} style={{ color: config.color }} title={config.label}>
          {config.stars}
        </span>
      );
    },
  };

  // A bookmark, not a star: ratingConfig already renders the LLM rating as a
  // five-star scale (one of whose colors is antd gold), and on mobile the two
  // columns sit adjacent — two star glyphs in the same color family meaning
  // unrelated things. Kept in BOTH column sets: the three-column phone layout,
  // where every column has to earn its width, is exactly where a saved marker
  // earns its keep.
  const savedColumn = {
    title: '',
    key: 'saved',
    // 63 on mobile, not 44: .compact forces in-row buttons to a 4.4rem (44px)
    // touch target and the td adds 0.8rem padding each side, plus the 3px saved
    // rule. A 44px declared width overhangs into the Rating column — and the
    // bookmark colliding with the five-star scale is the exact adjacency this
    // control was shaped to avoid. Desktop is table-layout:auto, so 44 is only
    // a hint: the column takes the ~64px it needs out of the flexible Contact
    // column. Only the mobile number is load-bearing.
    width: isMobile ? 63 : 44,
    render: (_: unknown, record: Opportunity) => {
      const saved = record.savedAt != null;
      return (
        <Tooltip title={saved ? 'Remove from saved' : 'Save for later'}>
          <Button
            type="text"
            // Carries the accessible focus ring — antd's default is a pale blue
            // that measures 1.74:1 on white (see the rule in the module).
            className={css.saveToggle}
            icon={saved ? <BookmarkFilled className={css.saveOn} /> : <BookmarkOutlined />}
            // Defensive: the drawer is opened by the title link and the View
            // button, not by a row click, so this is inert today. It keeps the
            // control correct if an onRow handler is ever added — opening the
            // drawer marks the row read, which saving must never do.
            onClick={(e) => { e.stopPropagation(); handleToggleSaved(record); }}
            // The ref guard already makes a second click inert; this is the
            // visible half of it, so the control doesn't invite a click it will
            // silently drop.
            disabled={inFlightIds.includes(record.id)}
            // The accessible name carries the state; deliberately no
            // aria-pressed. Pairing a changing, action-describing name with a
            // pressed state announces "Remove X from saved, pressed", which
            // reads as though removal — not saving — were the active state.
            aria-label={
              saved ? `Remove ${rowName(record)} from saved` : `Save ${rowName(record)}`
            }
          />
        </Tooltip>
      );
    },
  };

  const postedAge = (record: Opportunity) => {
    const age = getPostingAge(record.jobPostingDate);
    if (!age.valid) return null;
    return age.isStale ? (
      <Tooltip title={STALE_TOOLTIP}>
        <Tag icon={<ClockCircleOutlined />} color="warning" className={css.ageTag}>
          Posted {age.relative}
        </Tag>
      </Tooltip>
    ) : (
      <span className={css.ageMuted}>Posted {age.relative}</span>
    );
  };

  // The row's primary affordance, and on mobile the ONLY one — the View column
  // is dropped below the breakpoint (see mobileColumns). A real <button>, not
  // the `<a onClick>` this used to be: an anchor with no href is not in the tab
  // order and is not exposed as a control, so it was unusable by keyboard and
  // by assistive tech. It opens a drawer rather than navigating, so button is
  // also the honest element. Its text IS the accessible name.
  const titleAffordance = (title: string, record: Opportunity) => (
    <button type="button" onClick={() => handleViewDetails(record)} className={css.titleLink}>
      {title}
    </button>
  );

  // Users told us they can tell from the headline alone that a job is wrong for
  // them — usually the company. Making them open the drawer to say so taxes a
  // decision that is already made, and a few days of that turns the list into a
  // chore they stop opening. This is that decision, taken where it happens.
  //
  // A word, not an icon: the opposite verdict (the bookmark) is another control
  // in the same row, and an unlabelled ✕ beside a bookmark is the pair a thumb
  // gets wrong. It still opens the reason modal — that keeps passing no cheaper
  // than applying, keeps the rejection_reason data, and means a mis-tap costs a
  // Cancel rather than an Undo.
  const rejectAffordance = (record: Opportunity) => (
    <Button
      type="text"
      danger
      className={css.rowReject}
      // Same guard as the save toggle: inert today (no onRow handler), correct
      // if one is ever added. Rejecting must never open the drawer — opening it
      // would mark the row read, which is exactly what this path avoids.
      onClick={(e) => { e.stopPropagation(); openRowReject(record); }}
      aria-label={`Reject ${rowName(record)}`}
    >
      Reject
    </Button>
  );

  const actionsColumn = {
    title: '',
    key: 'actions',
    // Two controls now. Desktop is table-layout:auto, so this is a hint the
    // flexible Contact column absorbs.
    width: isMobile ? 56 : 180,
    render: (_: unknown, record: Opportunity) => (
      // Reject sits at the row's far right — the furthest this layout can put
      // it from the bookmark at the far left.
      <div className={css.rowActions}>
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          // Desktop-only now — this column is no longer in mobileColumns, so the
          // isMobile branches here are unreachable. Kept rather than stripped:
          // they are what a phone layout would need if this column ever earns its
          // width back, and they cost nothing meanwhile.
          aria-label={isMobile ? `View ${rowName(record)}` : undefined}
        >
          {isMobile ? '' : 'View'}
        </Button>
        {rejectAffordance(record)}
      </div>
    ),
  };

  // The desktop set (eight columns, 880px before the save column joined them)
  // does not fit a 358px phone scroller: it left only Rating and Title on
  // screen — the industry line was clipped and the View button sat
  // entirely off-canvas, so both of this branch's inbox features were invisible
  // on a phone. Below the breakpoint we drop Search / Location / Contact and
  // fold company + industry into the title cell, keeping the whole row within
  // the viewport. The unread treatment is a row-level class and a title weight,
  // so it survives untouched.
  //
  // View is dropped here too, and the save column takes its place — the row has
  // width for three columns, not four. On a phone the View button duplicates the
  // title: both open the same drawer, so it is the affordance to spend, and the
  // bookmark is the one that has no substitute. MEASURED, headless chromium at
  // 320/358/375 with the widest realistic row: four columns put the table at
  // 379.8px against scrollers of 288/326/343 — 92/54/37px of horizontal scroll,
  // with View itself off-canvas at 320 and 358. Three columns restore
  // 76 + 190 + 60 = 326 = the 358px scroller exactly, and 0px overflow at 358
  // and 375 (320 keeps a 32px overflow that predates all of this). Do not
  // re-add a fourth column here without re-measuring.
  // Select mode swaps the bookmark for the checkbox column rather than adding
  // one, so the count stays at three and the measurement above still holds:
  // antd's selection column is pinned to 50px (rowSelection.columnWidth), a
  // touch-sized stand-in for the 63px it replaces. While you are picking rows to
  // reject you are not saving them, so the bookmark is the one to spend.
  // Remote-search grades that want a look before applying. The drawer carries
  // the full note and the ad's sentence; the row only flags that there is one,
  // so a user scanning the list can tell a `likely`/`unstated` role from a
  // fully eligible one without opening every drawer.
  const eligibilityChip = (record: Opportunity) =>
    record.eligibility === 'likely' || record.eligibility === 'unstated' ? (
      <Tag color={record.eligibility === 'likely' ? 'blue' : 'orange'}>Check eligibility</Tag>
    ) : null;

  const contactStatusBadge = (record: Opportunity) => {
    const status = record.contactStatus;
    if (!status || status === 'ok') return null;
    // A status this build doesn't know says nothing rather than guess.
    const badge = contactStatusBadges[status];
    if (!badge) return null;
    return (
      <Tag color={badge.color} icon={badge.icon}>
        {badge.label}
      </Tag>
    );
  };

  const mobileColumns = [
    ...(selectMode ? [] : [savedColumn]),
    ratingColumn,
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: Opportunity) => (
        <div className={css.titleCell}>
          {titleAffordance(title, record)}
          <span className={css.companyLine}>{record.company.name}</span>
          <SourceTag source={record.source} sourceSite={record.sourceSite} />
          {eligibilityChip(record)}
          {contactStatusBadge(record)}
          {record.company.summary?.industry && (
            <span className={css.industryLine}>{record.company.summary.industry}</span>
          )}
          {/* Reject rides the posted-age line instead of taking a fourth column
              (the budget above is full) or a line of its own (which would grow
              every row on the page). In select mode it goes away: the bulk bar
              owns rejecting then, and two reject paths on one row is a mis-tap
              waiting to happen. */}
          <div className={css.titleMetaRow}>
            {postedAge(record)}
            {!selectMode && rejectAffordance(record)}
          </div>
        </div>
      ),
    },
  ];

  const desktopColumns = [
    savedColumn,
    ratingColumn,
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 420,
      render: (title: string, record: Opportunity) => (
        <div className={css.titleCell}>
          {titleAffordance(title, record)}
          <SourceTag source={record.source} sourceSite={record.sourceSite} />
          {eligibilityChip(record)}
          {contactStatusBadge(record)}
          {postedAge(record)}
        </div>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      width: 250,
      render: (_: unknown, record: Opportunity) => (
        <div className={css.companyCell}>
          <span>{record.company.name}</span>
          {record.company.summary?.industry && (
            <span className={css.industryLine}>{record.company.summary.industry}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Search',
      dataIndex: ['search', 'searchRole'],
      key: 'search',
      width: 200,
    },
    {
      title: 'Location',
      dataIndex: ['search', 'searchLocation'],
      key: 'location',
      width: 240,
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_: unknown, record: Opportunity) => (
        <span>
          {record.contact ? `${record.contact.firstName} ${record.contact.lastName}` : '—'}
        </span>
      ),
    },
    actionsColumn,
  ];

  const columns = isMobile ? mobileColumns : desktopColumns;

  const selectionCount = selectedRowKeys.length;
  // Desktop shows checkboxes always; a phone only inside select mode, where the
  // column is affordable. `undefined` (not a disabled config) so antd renders no
  // selection column at all outside those cases.
  const rowSelection = (!isMobile || selectMode) ? {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys as string[]),
    // Pinned rather than left to antd's default, because the phone budget is
    // measured to the pixel and this column is standing in for the 63px one.
    columnWidth: isMobile ? 50 : 48,
    // antd names its row checkboxes nothing at all, which down a list of twenty
    // reads as "checkbox, checkbox, checkbox". The job title is the only thing
    // that tells one row's checkbox from the next.
    //
    // The cast is load-bearing, not laziness: antd's CheckboxProps declares no
    // aria-* (TS2322), but rc-checkbox spreads every prop it does not recognise
    // straight onto the <input>, which is where a name has to land. `title` —
    // which IS declared — is not an alternative: rc-checkbox excludes it and
    // puts it on the wrapping <span>, naming nothing. Verified against the
    // rendered DOM, and antd names its own select-all checkbox the same way.
    getCheckboxProps: (record: Opportunity) =>
      ({ 'aria-label': `Select ${rowName(record)}` } as CheckboxProps),
  } : undefined;

  // Clears the ticks and nothing else. It deliberately does NOT leave select
  // mode on a phone: with zero ticked the filter bar comes back, Done is right
  // there, and the checkboxes stay — so one control keeps one job and re-picking
  // costs nothing. Clearing and then having to re-enter select mode was the
  // version that made you pay for a change of mind.
  const clearSelection = () => setSelectedRowKeys([]);

  // Replaces the filter bar rather than stacking under it: on a phone that
  // would cost a second row of chrome, and while a selection is live the Saved
  // filter is not what the user is reaching for.
  const bulkBar = (
    <div className={css.bulkBar}>
      {/* Live region: on a phone the checkboxes can be scrolled out of view, so
          the count is the only confirmation a tap registered. */}
      <span className={css.bulkCount} role="status">
        {selectionCount} selected
      </span>
      <div className={css.bulkActions}>
        {/* "Clear" is unambiguous beside the count it sits next to; the label
            spells it out for anyone who arrives without that context. */}
        <Button onClick={clearSelection} aria-label="Clear selection">Clear</Button>
        {/* Names its own scope, so the number the user is about to act on is on
            the button they press — not only in the label beside it. */}
        <Button danger type="primary" onClick={openBulkReject}>
          Reject {selectionCount}
        </Button>
      </div>
    </div>
  );

  const clearSavedOnly = () => {
    setSavedOnly(false);
    setPage(1, pageSize);
  };

  // Same element in both the list and the saved-empty return: an early return
  // that dropped it would leave the user inside a filter with no way out.
  const filterBar = (
    <div className={css.filterBar}>
      <Button
        type={savedOnly ? 'primary' : 'default'}
        // aria-hidden, because @ant-design/icons puts `aria-label="book"` on
        // every icon span: unhidden it prepends "book" to this button's
        // accessible name, which is computed from its contents (unlike the row
        // control, whose explicit aria-label overrides them).
        icon={savedOnly ? <BookmarkFilled aria-hidden /> : <BookmarkOutlined aria-hidden />}
        onClick={() => { setSavedOnly(v => !v); setPage(1, pageSize); }}
      >
        Saved{savedCount > 0 ? ` (${savedCount})` : ''}
        {/* Same rule as the row control: the accessible name carries the state,
            not aria-pressed. Sighted users read "on" from the fill and the
            colour; this is the only cue a screen reader gets. Appended to the
            visible label rather than replacing it via aria-label, so the name
            still contains what is on screen (WCAG 2.5.3 Label in Name). */}
        {savedOnly && <span className={css.srOnly}>, showing saved only</span>}
      </Button>
      {/* Phones only. Desktop has the width to show checkboxes permanently, so
          a mode toggle there would be a control that does nothing but hide
          another control.

          TRAILING edge, which is the rule this bar's own styles already state:
          leading reads as "a filter over the list below", trailing reads as "an
          action on it". Saved narrows what the list SHOWS; this changes what you
          can DO to it. Sat beside Saved in matching chrome it read as a second
          filter, and a user hunting for bulk actions did not find it.

          The checkbox glyph is not decoration — it previews the outcome
          literally: press this and that column becomes checkboxes. Outlined →
          filled is the vocabulary this file already owns (the row bookmark), so
          the on-state is a convention the user has met rather than a new one.

          aria-hidden for the same reason the Saved toggle does it:
          @ant-design/icons puts aria-label="check-square" on every icon span,
          which would prepend "check square" to this button's computed name. */}
      {isMobile && (
        <Button
          className={css.selectToggle}
          type={selectMode ? 'primary' : 'default'}
          icon={selectMode ? <CheckSquareFilled aria-hidden /> : <CheckSquareOutlined aria-hidden />}
          onClick={() => { setSelectMode(v => !v); setSelectedRowKeys([]); }}
        >
          {selectMode ? 'Done' : 'Select'}
        </Button>
      )}
    </div>
  );

  // Only the list area switches between loading, empty and the table. The
  // drawer and the reject modal below always render: an open opportunity is
  // pinned (see openedOpportunity) and its editor may hold unsaved edits, so a
  // refresh that comes back empty must never unmount them. "Empty" means the
  // total under the active filter is 0; an empty page past the end is moved
  // back (see the effect above) and shows the table meanwhile.
  const listEmpty = opportunities.length === 0 && total === 0;
  return (
    <div className={`${css.opportunitiesTab} ${isMobile ? css.compact : ''}`.trim()}>
      {loading && opportunities.length === 0 ? (
        <>
          {/* The toggle has to survive this frame too. Unsaving the last saved
              row empties the list and starts a refetch in the same commit, so
              a bare spinner would blink the only way out of the filter off
              the screen. */}
          {savedOnly && filterBar}
          <div className={css.loadingContainer}>
            <Spin size="large" />
          </div>
        </>
      ) : listEmpty && savedOnly ? (
        <>
          {/* An active filter over a non-empty list is not an empty account. "No
             opportunities yet" would tell the user their searches found nothing, which
             is both wrong and alarming — so this branch has to come first.

             The copy says "to show", not "nothing saved": server-side `saved_only` ANDs
             with the caller's min_fit_level, so a user who saved a potential_candidate
             role and later raised their threshold lands here with those rows still
             saved in the database. Telling them to go and save something would be a lie
             AND a wasted instruction. The envelope cannot separate "saved nothing" from
             "saved, but below threshold" — and rather than buy a second count for it,
             the copy is written to be true either way and to name the one reason rows
             go missing, so the user knows where to look. */}
          {filterBar}
          <div className={css.emptyContainer}>
            <Empty
              description="No saved opportunities to show"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <p>
                Opportunities you save appear here. Any that fall below your current fit level
                are hidden.
              </p>
              <Button onClick={clearSavedOnly}>Show all opportunities</Button>
            </Empty>
          </div>
        </>
      ) : listEmpty ? (
        <div className={css.emptyContainer}>
          <Empty
            description="No opportunities yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <p>Opportunities will appear here as we find matches for your searches.</p>
          </Empty>
        </div>
      ) : (
        <>
          {selectionCount > 0 ? bulkBar : filterBar}
          {/* Scoped to this table only — the tokens re-colour row selection, and
              nothing else on the page should inherit that. */}
          <ConfigProvider theme={TABLE_THEME}>
            <Table
              dataSource={opportunities}
              columns={columns}
              rowSelection={rowSelection}
              rowKey="id"
              loading={loading}
              // Email-client unread: tint + semibold title until first open. Saved
              // COMPOSES with it (a leading-edge rule, not a second wash) — a row can
              // be both.
              rowClassName={(record) =>
                [record.viewedAt == null ? css.rowUnread : '', record.savedAt != null ? css.rowSaved : '']
                  .filter(Boolean)
                  .join(' ')
              }
              onChange={(pagination) => {
                setPage(pagination.current ?? 1, pagination.pageSize ?? 10);
              }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                showTotal: (total) => `${total} opportunities`,
              }}
            />
          </ConfigProvider>
        </>
      )}

      <OpportunityDrawer
        opportunity={selectedOpportunity}
        open={drawerOpen}
        onClose={handleDrawerClose}
        onReject={() => {
          handleActionComplete('reject');
        }}
        onApply={() => {
          handleActionComplete();
        }}
        onRegenerated={handleRegenerated}
        loadFresh={handleDrawerLoadFresh}
        loadAfterWrite={handleDrawerWrite}
        onViewed={handleViewed}
        // Same handler as the row control, so the two can't disagree: it patches
        // the pinned drawer copy as well as the list row.
        onToggleSaved={handleToggleSaved}
        // The handler's in-flight guard makes a second click inert. The row
        // control already shows that by disabling; without the same cue here the
        // drawer takes the click, looks like it accepted it, and drops it — so
        // an immediate undo of a save silently leaves the row saved.
        savePending={
          selectedOpportunity ? inFlightIds.includes(selectedOpportunity.id) : false
        }
      />

      {/* The row and bulk paths share the drawer's modal, so the reason list,
          the Undo and the analytics stay identical wherever the decision is
          taken — only `source` distinguishes them. */}
      <RejectModal
        subjects={rejectSubjects}
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSuccess={handleRejectSuccess}
        source={rejectSource}
      />
    </div>
  );
};

export default OpportunitiesTab;
